import { Dispatch, SetStateAction, useState } from 'react';
import PanoramaFishEyeRoundedIcon from '@mui/icons-material/PanoramaFishEyeRounded';
import VerifiedUserOutlined from '@mui/icons-material/VerifiedUserOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import VaultExportGrid from './vault-export-grid';
import MatterLogHistory from './matter-log-history';
import {
  formatBytes,
  toCamelCase,
  getDeleteMatterPermissionState,
} from '../../../utils/vault-utils';
import { ExtendedVaultMatterModel } from './vault-export-page';
import {
  isAbleToSeeUIElement,
  VaultRoles,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import IosShare from '@mui/icons-material/IosShare';
import IosShareOutlined from '@mui/icons-material/IosShareOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import Storage from '@mui/icons-material/Storage';
import SharedTabCard from '../../../shared/shared-tab-card';

interface MatterDetailsProps {
  matter: ExtendedVaultMatterModel;
  onBack: () => void;
  onDelete: () => void;
  onEdit: () => void;
  toolsRoles: string[];
  openAddVaultExport: boolean;
  setOpenAddVaultExport: Dispatch<SetStateAction<boolean>>;
}

export default function MatterDetails({
  matter,
  onDelete,
  onEdit,
  toolsRoles,
  openAddVaultExport,
  setOpenAddVaultExport,
}: Readonly<MatterDetailsProps>) {
  const [activeTab, setActiveTab] = useState(0);
  const statusColor =
    matter.state === 'OPEN'
      ? 'success.main'
      : matter.state === 'CLOSED'
      ? 'text.secondary'
      : 'error.main';

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const deleteState = getDeleteMatterPermissionState(toolsRoles);

  return (
    <Stack sx={{ height: '100%', overflowY: 'auto', pr: 1 }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          py: 3,
          px: 0,
          borderRadius: 2,
          boxShadow: 1,
          border: '1px solid',
          borderColor: 'divider',
          mb: 2,
        }}
      >
        {/* Title Block: folder icon, matter name, status chip, and description */}
        <Stack direction="row" alignItems="stretch" spacing={2} sx={{ ml: 3 }}>
          <Box
            sx={{
              width: 48,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FolderOutlinedIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ flexWrap: 'wrap', mb: 0.5 }}
            >
              <Typography
                noWrap
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {matter.name}
              </Typography>
              <Chip
                icon={
                  <PanoramaFishEyeRoundedIcon
                    fontSize="small"
                    color="inherit"
                  />
                }
                label={toCamelCase(matter.state)}
                size="small"
                sx={{
                  fontWeight: 600,
                  color: statusColor,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: `${statusColor}33`,
                }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {matter.description || 'No description'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', pr: 3, gap: 1 }}>
            <Tooltip
              arrow
              title={
                isAbleToSeeUIElement(
                  toolsRoles,
                  VaultRoles.EditVaultMatters.value
                )
                  ? 'Edit'
                  : Messages.Errors.Permission.Acting(
                      VaultRoles.EditVaultMatters.name
                    )
              }
            >
              <span>
                <IconButton
                  disabled={
                    !isAbleToSeeUIElement(
                      toolsRoles,
                      VaultRoles.EditVaultMatters.value
                    )
                  }
                  onClick={onEdit}
                  size="small"
                >
                  <Edit />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              arrow
              title={deleteState.canDelete ? 'Delete' : deleteState.tooltip}
            >
              <span>
                <IconButton
                  disabled={!deleteState.canDelete}
                  onClick={onDelete}
                  size="small"
                >
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Metrics Block: Total exports and data size positioned vertically below the title block, designed like Last Sign In and its sub data */}
        <Grid container spacing={3} alignItems="center" sx={{ ml: 3 }}>
          <Grid>
            <Typography
              variant="body2"
              sx={{ fontWeight: 'bold', color: 'text.secondary' }}
            >
              <IosShare fontSize="inherit" sx={{ verticalAlign: 'text-top' }} />{' '}
              Total Exports:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mt: 0.5 }}
            >
              {matter.exportCount ?? 0}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              variant="body2"
              sx={{ fontWeight: 'bold', color: 'text.secondary' }}
            >
              <Storage fontSize="inherit" sx={{ verticalAlign: 'text-top' }} />{' '}
              Data Size:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mt: 0.5 }}
            >
              {formatBytes(matter.dataSizeBytes)}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <SharedTabCard>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{
            mx: 1,
            '& .MuiTabs-flexContainer': {
              marginTop: '6px',
            },
            '.MuiTabs-scrollButtons.Mui-disabled': {
              opacity: 0.3,
            },
          }}
        >
          <Tab
            icon={<IosShareOutlined />}
            iconPosition="start"
            label="Exports"
            sx={{ minHeight: 0, textTransform: 'none' }}
          />
          <Tab
            icon={<HistoryOutlined />}
            iconPosition="start"
            label="Log History"
            sx={{ minHeight: 0, textTransform: 'none' }}
          />
          <Tab
            icon={<SearchOutlined />}
            iconPosition="start"
            label="Search (Soon)"
            disabled
            sx={{ minHeight: 0, textTransform: 'none' }}
          />
          <Tab
            icon={<VerifiedUserOutlined />}
            iconPosition="start"
            label="Holds (Soon)"
            disabled
            sx={{ minHeight: 0, textTransform: 'none' }}
          />
        </Tabs>
      </SharedTabCard>

      <Box sx={{ flexGrow: 1, minHeight: 400 }}>
        {activeTab === 0 && (
          <VaultExportGrid
            toolsRoles={toolsRoles}
            matterId={matter.matterId}
            matterName={matter.name}
            openAddVaultExport={openAddVaultExport}
            setOpenAddVaultExport={setOpenAddVaultExport}
          />
        )}
        {activeTab === 1 && <MatterLogHistory matterId={matter.matterId} />}
      </Box>
    </Stack>
  );
}
